import { useRef, useState } from 'react'
import { useAuthDataStore } from '@entities/user'
import { ewalletReqHeaders } from '@shared/config/constants'
import { ewalletApi } from '@root/api/api'
import { TransferTermsRs } from '@root/api/ump-ewallet'
import { useCyberStore } from '@entities/tsp'
import { PaymentMethod } from '@entities/payment'

interface GetPaymentTermsReqProps {
  serviceId: string
  amount: number
  currency: number
  bindingId?: string
  pan?: string
  expiry?: string
  cardholderName?: string
  cvc?: string
  serviceParams?: string
  comment?: string
  masterpassOperationId?: string
  onSuccess?: (data: TransferTermsRs) => void
  onError?: (error) => void
}

export const useGetPaymentTerms = () => {
  const [isLoading, setIsLoading] = useState(false)
  const getPaymentTermsRequestAbortController = useRef(new AbortController())
  const { isAuth, profile } = useAuthDataStore()
  const { paymentMethod } = useCyberStore()
  const userId = profile?.userId

  const cancelGetPaymentTermsRequest = () => {
    getPaymentTermsRequestAbortController.current.abort()
    getPaymentTermsRequestAbortController.current = new AbortController()
  }

  const getPaymentTerms = async ({
    currency,
    amount,
    serviceId,
    bindingId,
    pan,
    expiry,
    cardholderName,
    cvc,
    serviceParams,
    comment,
    masterpassOperationId,
    onSuccess,
    onError,
  }: GetPaymentTermsReqProps) => {
    getPaymentTermsRequestAbortController.current.abort()
    getPaymentTermsRequestAbortController.current = new AbortController()

    setIsLoading(true)

    try {
      const { data } = await ewalletApi.getPaymentsTermsDo(
        paymentMethod === PaymentMethod.SBP || !isAuth ? 'anonymous' : 'b',
        serviceId,
        amount,
        currency,
        userId,
        bindingId,
        pan,
        expiry,
        cardholderName,
        cvc,
        serviceParams,
        comment,
        masterpassOperationId,
        {
          signal: getPaymentTermsRequestAbortController.current.signal,
          headers: {
            ...ewalletReqHeaders,
            fhprequestid: window?.gibRequestId,
            fhpsessionid: window?.gibSessionId,
          },
        }
      )

      if (!data.errorMessage) {
        if (onSuccess) onSuccess(data)
      } else if (onError) {
        onError(new Error(data.errorMessage))
      }
    } catch (error) {
      if (onError && error?.code !== 'ERR_CANCELED') onError(error)
    } finally {
      setIsLoading(false)
    }
  }

  return { getPaymentTerms, cancelGetPaymentTermsRequest, isLoading }
}
